import React, { useRef, useState } from 'react';

import styled from 'styled-components';

import { CommunityActivityType } from 'types/communities';
import useInfiniteScroll from 'utils/hooks/useInfiniteScroll';

import ActivityItem from './ActivityItem';

type ActivityFeedProps = {
  activities: CommunityActivityType[];
  communityId?: number;
  hasNextPage?: boolean;
  isFetching?: boolean;
  isFetchingNextPage?: boolean;
  isLoading?: boolean;
  emptyText?: string;
  onLoadMore?: () => void;
  onActivityUpdated?: () => void | Promise<unknown>;
};

const FeedContainer = styled.div`
  overflow-y: auto;
  box-sizing: border-box;
  padding: 30px 32px;
  scrollbar-gutter: stable;
`;

const FeedContent = styled.div`
  width: 100%;
  box-sizing: border-box;
`;

const Feed = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  min-width: 0;
`;

const EmptyState = styled.div`
  padding: 20px 0;
  color: var(--text-secondary);
  font-size: 14px;
  text-align: center;
`;

const LoadingState = styled(EmptyState)`
  color: var(--text-primary);
`;

const LoadMoreTrigger = styled.div`
  min-height: 1px;
`;

const LoadMoreState = styled(EmptyState)`
  padding: 12px 0 0;
`;

const ActivityFeed = ({
  activities,
  communityId,
  hasNextPage = false,
  isFetching = false,
  isFetchingNextPage = false,
  isLoading = false,
  emptyText = 'No activities have been recorded yet.',
  onLoadMore,
  onActivityUpdated,
}: ActivityFeedProps) => {
  const feedContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const [renderedActivityIds, setRenderedActivityIds] = useState<Record<number, boolean>>({});

  const toggleRenderedBody = (activityId: number) => {
    setRenderedActivityIds((currentState) => ({
      ...currentState,
      [activityId]: !currentState[activityId],
    }));
  };

  useInfiniteScroll({
    rootRef: feedContainerRef,
    triggerRef: loadMoreTriggerRef,
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
  });

  if (isLoading && activities.length === 0) {
    return <LoadingState>Loading activities...</LoadingState>;
  }

  if (activities.length === 0) {
    return <EmptyState>{emptyText}</EmptyState>;
  }

  return (
    <FeedContainer ref={feedContainerRef} className="mini-scroll">
      <FeedContent>
        <Feed>
          {activities.map((activity) => (
            <ActivityItem
              key={activity.id}
              activity={activity}
              communityId={communityId}
              showRenderedBody={Boolean(renderedActivityIds[activity.id])}
              onToggleRenderedBody={() => toggleRenderedBody(activity.id)}
              onActivityUpdated={onActivityUpdated}
            />
          ))}
          {hasNextPage && <LoadMoreTrigger ref={loadMoreTriggerRef} />}
          {isFetchingNextPage && <LoadMoreState>Loading more activities...</LoadMoreState>}
          {!isFetchingNextPage && isFetching && <LoadMoreState>Refreshing activities...</LoadMoreState>}
        </Feed>
      </FeedContent>
    </FeedContainer>
  );
};

export default ActivityFeed;
